import type { ProgressSummary } from '../../lib/dashboard/progress';
import { describeChange } from './change-wording';
import { formatChangeNumber, metricChange } from '../lib/metric-format';

const SEGMENTS = 10;

const Stat: React.FC<{ value: string; unit: string; label: string; testId?: string }> = ({
  value,
  unit,
  label,
  testId,
}) => (
  <div data-testid={testId}>
    <div className="tabular text-2xl font-semibold leading-none tracking-tight">
      {value} <span className="font-mono text-xs text-primary-foreground/70">{unit}</span>
    </div>
    <div className="mt-1.5 text-[11px] text-primary-foreground/70">{label}</div>
  </div>
);

const shortDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const Eyebrow: React.FC = () => (
  <div className="font-mono text-[9px] font-medium uppercase tracking-widest text-primary-foreground/70">
    Change quality · since start
  </div>
);

/**
 * The sage strip: one sentence of change quality, then the figures behind it.
 * The concept puts this directly under the hero because it is the question a
 * coach opens the client with.
 *
 * Change quality — how much of what was lost was fat — is a kilogram
 * question, and needs a weight on the scans. Without one the strip used to
 * vanish; it now says what the tape can: points of body fat and centimetres
 * of waist, and why the kilograms are missing.
 */
export const ChangeSinceStart: React.FC<{
  /** The kilogram summary, or null when too few scans were weighed. */
  summary: ProgressSummary | null;
  /** Body fat at the first and latest scan, from the tape — no weight needed. */
  bodyFat: { start: number; now: number };
  /** Measured waist in centimetres, first scan and latest, and the shared change. */
  waist?: { start?: number; now?: number; change?: number };
  /**
   * When the latest scan was not weighed, the kilogram figures stop at an
   * earlier one. Its date, so the strip does not present them as "since
   * start" to today.
   */
  weighedThrough?: Date;
  className?: string;
}> = ({ summary, bodyFat, waist, weighedThrough, className = '' }) => {
  // From the shared pair (metricPair), rounded as displayed — the same
  // figure the hero, the Avatar tape list and the Measurements card show.
  const waistDelta =
    waist?.change !== undefined
      ? waist.change
      : waist?.start !== undefined && waist?.now !== undefined
        ? metricChange('waist', waist.start, waist.now)
        : null;
  const waistStat = waistDelta !== null && (
    <Stat value={formatChangeNumber('waist', waistDelta)} unit="cm" label="Waist" testId="change-waist" />
  );
  const points = metricChange('body_fat', bodyFat.start, bodyFat.now);

  if (!summary) {
    return (
      <div
        data-testid="change-since-start"
        className={`flex flex-col gap-5 rounded-lg bg-primary p-5 text-primary-foreground lg:grid lg:items-center lg:grid-cols-[minmax(0,1.6fr)_repeat(2,minmax(0,1fr))] ${className}`}
      >
        <div>
          <Eyebrow />
          <div className="mt-1 text-lg font-semibold">
            {points < 0
              ? `${Math.abs(points).toFixed(1)} points of body fat lost`
              : points > 0
                ? `${points.toFixed(1)} points of body fat gained`
                : 'Body fat unchanged since start'}
          </div>
          <div data-testid="change-unweighed" className="mt-1.5 text-[10px] text-primary-foreground/70">
            The scans were not weighed, so fat and lean cannot be split in kilograms.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-5 lg:contents">
          <Stat value={formatChangeNumber('body_fat', points)} unit="pts" label="Body fat" testId="change-body-fat" />
          {waistStat}
        </div>
      </div>
    );
  }

  const { changeQuality } = summary;
  // Weight and lean by the rule the hero uses for the same two scans, so its
  // Now column and this strip cannot differ by a rounding tenth.
  const leanOf = (p: ProgressSummary['now']) => p.leanKg;
  const weightOf = (p: ProgressSummary['now']) => p.fatKg + leanOf(p);
  const kgChange = (of: (p: ProgressSummary['now']) => number) =>
    metricChange('weight', of(summary.start), of(summary.now));
  const filled = Math.round(changeQuality * SEGMENTS);
  // The sentence states the same rounded figures as the cells beside it.
  const wording = describeChange({
    ...summary,
    fatKgDelta: kgChange((p) => p.fatKg),
    leanKgDelta: kgChange(leanOf),
    weightKgDelta: kgChange(weightOf),
  });

  return (
    <div
      data-testid="change-since-start"
      className={`flex flex-col gap-5 rounded-lg bg-primary p-5 text-primary-foreground lg:grid lg:items-center ${
        // One class, not two: both are arbitrary-value utilities of equal
        // specificity, so which wins would come down to stylesheet order.
        waistDelta !== null
          ? 'lg:grid-cols-[minmax(0,1.6fr)_repeat(4,minmax(0,1fr))]'
          : 'lg:grid-cols-[minmax(0,1.6fr)_repeat(3,minmax(0,1fr))]'
      } ${className}`}
    >
      <div>
        <Eyebrow />
        <div data-testid="change-headline" className="mt-1 text-lg font-semibold">{wording.headline}</div>
        {/* How much of a loss was fat. A gain or a recomposition has no
            deficit to divide, and showed "0 %" here. */}
        {wording.showQualityBar && (
        <div className="mt-2.5 flex gap-[3px]" role="presentation">
          {Array.from({ length: SEGMENTS }, (_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-sm ${
                i < filled ? 'bg-primary-foreground' : 'bg-primary-foreground/25'
              }`}
            />
          ))}
        </div>
        )}
        <div className="mt-1.5 text-[10px] text-primary-foreground/70">
          {wording.detail}
          {weighedThrough && `, to ${shortDate(weighedThrough)} — the last weighed scan`}
        </div>
      </div>
      {/* Two-up on a phone, one row of cells on desktop. `display: contents`
          drops the wrapper out of the grid so the same Stat elements become
          direct grid children rather than one crowded cell. */}
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-4 lg:contents">
        <Stat value={formatChangeNumber('weight', kgChange((p) => p.fatKg))} unit="kg" label="Fat mass" />
        <Stat value={formatChangeNumber('weight', kgChange(leanOf))} unit="kg" label="Lean mass" />
        <Stat value={formatChangeNumber('weight', kgChange(weightOf))} unit="kg" label="Weight" />
        {waistStat}
      </div>
    </div>
  );
};
